import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faUserGraduate,
  faClipboardList,
  faPlus,
  faTimes,
  faFlag,
  faArrowLeft,
  faEye,
  faEyeSlash,
  faBullhorn,
  faComments,
  faVideo,
  faBriefcase,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import ModuleMarksPanel from '../../components/course/ModuleMarksPanel';
import { useAuth } from '../../contexts/AuthContext';
import AnnouncementsPanel from '../../components/course/AnnouncementsPanel';
import DiscussionsPanel from '../../components/course/DiscussionsPanel';
import WebinarsPanel from '../../components/course/WebinarsPanel';
import WorkplaceStaffPanel from '../../components/course/WorkplaceStaffPanel';
import { MODULE_TYPE_BADGE, MODULE_TYPE_LABELS } from '../../utils/http';

interface InstructorStudent {
  userId: string;
  studentRecordId: string;
  enrollmentId: string;
  studentId: string;
  fullName: string;
  email: string;
  profilePictureUrl: string | null;
  overallProgressPercent: number;
  enrollmentStatus: number;
  averageGrade: number | null;
  overrideGrade: number | null;
  isFlagged: boolean;
}

interface AssignmentSummary {
  assignmentId: string;
  title: string;
  dueDate: string;
  maxMark: number;
  isPublished: boolean;
  submittedCount: number;
  gradedCount: number;
  totalStudents: number;
  assessmentType: number;
}

type TabType = 'students' | 'assignments' | 'marks' | 'announcements' | 'discussions' | 'sessions' | 'workplace';

export default function InstructorCourseDetailPage() {
  const { learnershipId } = useParams<{ learnershipId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [activeTab, setActiveTab] = useState<TabType>('students');
  const [students, setStudents] = useState<InstructorStudent[]>([]);
  const [assignments, setAssignments] = useState<AssignmentSummary[]>([]);
  const [courseTitle, setCourseTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [maxMark, setMaxMark] = useState('100');
  const [passingMark, setPassingMark] = useState('50');
  const [assessmentType, setAssessmentType] = useState('0');
  const [criteria, setCriteria] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnershipId]);

  const fetchData = async () => {
    if (!learnershipId) return;
    try {
      setIsLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [studentsRes, assignmentsRes, coursesRes] = await Promise.all([
        fetch(`/api/instructor/courses/${learnershipId}/students`, { headers }),
        fetch(`/api/assignments/course/${learnershipId}`, { headers }),
        fetch('/api/instructor/courses', { headers }),
      ]);

      if (!studentsRes.ok || !assignmentsRes.ok) throw new Error('Failed to load course data');

      const studentsData = await studentsRes.json();
      setStudents(studentsData);
      setAssignments(await assignmentsRes.json());

      if (coursesRes.ok) {
        const courses = await coursesRes.json();
        const match = courses.find((c: any) => c.learnershipId === learnershipId);
        if (match) setCourseTitle(match.title);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load course data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setInstructions('');
    setDueDate('');
    setMaxMark('100');
    setPassingMark('50');
    setAssessmentType('0');
    setCriteria('');
    setFormError('');
  };

  const handleCreateAssignment = async () => {
    setFormError('');
    if (!title.trim() || !dueDate) {
      setFormError('Title and due date are required.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch('/api/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          learnershipId,
          title,
          instructions,
          dueDate: new Date(dueDate).toISOString(),
          maxMark: parseInt(maxMark, 10) || 100,
          passingMark: parseInt(passingMark, 10) || 50,
          assessmentType: parseInt(assessmentType, 10),
          criteria: criteria.trim() || null,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setFormError(data.message || 'Failed to create assignment.');
        return;
      }

      setShowCreateModal(false);
      resetForm();
      await fetchData();
      setActiveTab('assignments');
    } catch (err) {
      console.error(err);
      setFormError('An error occurred while creating the assignment.');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePublish = async (assignmentId: string, isPublished: boolean) => {
    try {
      await fetch(`/api/assignments/${assignmentId}/publish?isPublished=${!isPublished}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate('/instructor/courses')}
          className="flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 mb-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to courses
        </button>
        <h1 className="text-2xl font-bold text-brand-950">{courseTitle || 'Course'}</h1>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="flex gap-1 border-b border-canvas-300 bg-white rounded-t-2xl mb-6 overflow-x-auto">
              {[
                { id: 'students', label: 'Students', icon: faUserGraduate },
                { id: 'assignments', label: 'Assessments', icon: faClipboardList },
                { id: 'marks', label: 'Module Marks', icon: faPenToSquare },
                { id: 'announcements', label: 'Announcements', icon: faBullhorn },
                { id: 'discussions', label: 'Discussions', icon: faComments },
                { id: 'sessions', label: 'Live Sessions', icon: faVideo },
                { id: 'workplace', label: 'Workplace Log', icon: faBriefcase },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                    activeTab === tab.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'students' && (
              <div className="card-surface overflow-hidden">
                {students.length === 0 ? (
                  <div className="p-12 text-center">
                    <FontAwesomeIcon icon={faUserGraduate} className="h-12 w-12 text-brand-300 mb-4" />
                    <p className="text-brand-900/60">No students enrolled in this course yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
                      <span>Student</span>
                      <span>Progress</span>
                      <span>Average</span>
                      <span>Status</span>
                      <span></span>
                    </div>
                    <div className="divide-y divide-canvas-200">
                      {students.map((s) => (
                        <div
                          key={s.enrollmentId}
                          className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                        >
                          <div className="min-w-0 flex items-center gap-2">
                            {s.isFlagged && (
                              <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5 text-red-500 flex-shrink-0" title="Flagged for follow-up" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-brand-950 truncate">{s.fullName}</p>
                              <p className="text-xs text-brand-900/50 truncate">{s.studentId}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-canvas-200 rounded-full h-1.5">
                              <div className="bg-brand-gradient h-1.5 rounded-full" style={{ width: `${s.overallProgressPercent}%` }} />
                            </div>
                            <span className="text-xs text-brand-900/60">{s.overallProgressPercent}%</span>
                          </div>
                          <p className="text-sm text-brand-900/70">
                            {s.overrideGrade != null
                              ? `${s.overrideGrade}% (override)`
                              : s.averageGrade != null
                                ? `${s.averageGrade}%`
                                : '—'}
                          </p>
                          <span
                            className={`inline-block w-fit px-2.5 py-1 rounded-full text-xs font-semibold ${
                              s.enrollmentStatus === 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {s.enrollmentStatus === 0 ? 'In Progress' : s.enrollmentStatus === 1 ? 'Completed' : 'Other'}
                          </span>
                          <Link
                            to={`/instructor/students/${s.studentRecordId}`}
                            className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors w-fit"
                          >
                            View
                          </Link>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'assignments' && (
              <div>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                  >
                    <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
                    New Assignment
                  </button>
                </div>

                {assignments.length === 0 ? (
                  <div className="card-surface p-12 text-center">
                    <FontAwesomeIcon icon={faClipboardList} className="h-12 w-12 text-brand-300 mb-4" />
                    <p className="text-brand-900/60">No assignments created for this course yet.</p>
                  </div>
                ) : (
                  <div className="card-surface overflow-hidden">
                    <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
                      <span>Assignment</span>
                      <span>Due</span>
                      <span>Submissions</span>
                      <span>Status</span>
                      <span></span>
                    </div>
                    <div className="divide-y divide-canvas-200">
                      {assignments.map((a) => (
                        <div
                          key={a.assignmentId}
                          className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-brand-950 truncate">{a.title}</p>
                            <p className="text-xs text-brand-900/50 flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${MODULE_TYPE_BADGE[a.assessmentType] ?? ''}`}>
                                {MODULE_TYPE_LABELS[a.assessmentType] ?? 'Knowledge'}
                              </span>
                              Max {a.maxMark} marks
                            </p>
                          </div>
                          <p className="text-sm text-brand-900/70">{formatDate(a.dueDate)}</p>
                          <p className="text-sm text-brand-900/70">
                            {a.submittedCount}/{a.totalStudents} submitted · {a.gradedCount} graded
                          </p>
                          <span
                            className={`inline-block w-fit px-2.5 py-1 rounded-full text-xs font-semibold ${
                              a.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {a.isPublished ? 'Published' : 'Draft'}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => togglePublish(a.assignmentId, a.isPublished)}
                              className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                              title={a.isPublished ? 'Unpublish' : 'Publish'}
                            >
                              <FontAwesomeIcon icon={a.isPublished ? faEyeSlash : faEye} className="h-3 w-3" />
                            </button>
                            <Link
                              to={`/instructor/assignments/${a.assignmentId}`}
                              className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all"
                            >
                              Grade
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {learnershipId && activeTab === 'marks' && <ModuleMarksPanel learnershipId={learnershipId} />}
            {learnershipId && activeTab === 'announcements' && (
              <AnnouncementsPanel learnershipId={learnershipId} canManage />
            )}
            {learnershipId && activeTab === 'discussions' && <DiscussionsPanel learnershipId={learnershipId} />}
            {learnershipId && activeTab === 'sessions' && <WebinarsPanel learnershipId={learnershipId} canManage />}
            {learnershipId && activeTab === 'workplace' && <WorkplaceStaffPanel learnershipId={learnershipId} />}
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">New Assignment</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Network Configuration Lab"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Instructions</label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  placeholder="What should students do?"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Assessment type</label>
                <select
                  value={assessmentType}
                  onChange={(e) => setAssessmentType(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="0">Knowledge</option>
                  <option value="1">Practical</option>
                  <option value="2">Workplace</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Assessment criteria</label>
                <textarea
                  value={criteria}
                  onChange={(e) => setCriteria(e.target.value)}
                  rows={3}
                  placeholder="How will this be marked? Visible to learners and moderators."
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Max Mark</label>
                  <input
                    type="number"
                    value={maxMark}
                    onChange={(e) => setMaxMark(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Passing Mark</label>
                  <input
                    type="number"
                    value={passingMark}
                    onChange={(e) => setPassingMark(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAssignment}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                {isSaving ? 'Creating...' : 'Create Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
